import { toneNumberSyllableForComparison } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";

/**
 * The line under a pronunciation card's glyph that singles out the reading being asked for.
 * Its typography follows what it is: a gloss is bold (glosses are unique, so bold always means
 * "this character's gloss"), a mnemonic would be italic, and any other text is regular.
 *
 * `onlyInWords` is set when the reading occurs in no other word (会 kuài: only in 会计); the
 * review renders it as "only in 会计" under the hint.
 */
export type PronunciationHint = (
  | { kind: "gloss"; text: string }
  | { kind: "text"; text: string }
) & {
  onlyInWords?: readonly string[];
};

/**
 * The main reading's hint is the character's own gloss (大 dà → **big**). A secondary reading
 * shows its own reading gloss as plain text (大 dài → `doctor`), since the card gloss would not
 * tell it apart from the main reading. A reading whose grapheme-file `usage` is exhaustive also
 * names the words it is confined to (大 dài → only in 大夫).
 */
export function resolvePronunciationHint({
  cardGloss,
  mainPinyin,
  pinyin,
  pinyinList,
}: {
  /** The meaning card's gloss. */
  cardGloss: string | null | undefined;
  /**
   * The character's main reading in the words curriculum; undefined for a single-reading
   * character.
   */
  mainPinyin: string | undefined;
  /** The drilled reading, in tone-number form. */
  pinyin: string;
  pinyinList: readonly {
    gloss?: string | null;
    toneNumbersPinyin: string;
    usage?: { exhaustive: boolean; words: readonly string[] } | null;
  }[];
}): PronunciationHint | null {
  const drilledPinyin = toneNumberSyllableForComparison(pinyin);
  const isDrilledPinyin = (other: string) =>
    toneNumberSyllableForComparison(other) === drilledPinyin;

  const drilledEntry = pinyinList.find((entry) => isDrilledPinyin(entry.toneNumbersPinyin));
  const onlyInWords = drilledEntry?.usage?.exhaustive === true ? drilledEntry.usage.words : [];
  const withUsage = (hint: PronunciationHint): PronunciationHint =>
    onlyInWords.length === 0 ? hint : { ...hint, onlyInWords };

  const studiedPinyinList = pinyinList.filter(({ gloss }) => gloss != null && gloss !== "");
  const isMainPinyin =
    mainPinyin === undefined ? studiedPinyinList.length <= 1 : isDrilledPinyin(mainPinyin);
  if (isMainPinyin && cardGloss != null && cardGloss !== "") {
    return withUsage({ kind: "gloss", text: cardGloss });
  }

  const readingGloss = drilledEntry?.gloss;
  return readingGloss == null || readingGloss === ""
    ? null
    : withUsage({ kind: "text", text: readingGloss });
}
